// v1.5.0 · Neuromatemáticas JASQ: plataforma educativa que cruza cómo se siente cada estudiante con cuánto sabe,
//           para armar un perfil personalizado (5 pantallas animadas). Saber = azul, sentir = naranja.
import React from 'react';
import { useTick, Count, Head, Live, Kpi, Status, wave } from './kit';

const SABER = '#2E9BD6';
const SENTIR = '#C7811F';
const Legend = () => (
  <div className="flex gap-3 text-[10.5px]">
    <span className="flex items-center gap-1"><i className="h-2 w-2 rounded-sm" style={{ background: SABER }} />Cómo sabe</span>
    <span className="flex items-center gap-1"><i className="h-2 w-2 rounded-sm" style={{ background: SENTIR }} />Cómo se siente</span>
  </div>
);

/* 1 · Matriz sentir vs saber */
const STUDENTS = Array.from({ length: 22 }, (_, i) => ({ x: 18 + ((i * 37) % 70), y: 15 + ((i * 53) % 72) }));
export function NeuroMatriz() {
  const t = useTick(2000);
  const k = Math.min(1, (t % 10) / 8);                       // Sofía mejora su bienestar
  const sofia = { x: 74, y: 30 + k * 42 };
  const X = (v) => 30 + (v / 100) * 400; const Y = (v) => 270 - (v / 100) * 250;
  const q = (s) => (s.x >= 50 ? (s.y >= 50 ? 0 : 1) : (s.y >= 50 ? 2 : 3));
  const all = [...STUDENTS.map((s, i) => ({ x: s.x + wave(t, i + 1, 2), y: s.y + wave(t, i + 2, 2) })), sofia];
  const counts = [0, 0, 0, 0]; all.forEach((s) => { counts[q(s)] += 1; });
  const Q = [['Seguro y competente', 'ok'], ['Sabe, pero con ansiedad', 'warn'], ['Motivado, le falta base', 'info'], ['Necesita acompañamiento', 'bad']];
  return (
    <div className="grid h-full grid-cols-1 gap-3 p-3 sm:grid-cols-[1.6fr_1fr]">
      <div className="dash-panel min-h-0 p-2">
        <svg viewBox="0 0 450 295" className="h-full w-full" role="img" aria-label="Matriz de estudiantes: dominio de matemáticas frente a bienestar emocional">
          <rect x={X(50)} y={Y(100)} width={X(100) - X(50)} height={Y(50) - Y(100)} fill="rgba(79,191,106,.07)" />
          <rect x={X(50)} y={Y(50)} width={X(100) - X(50)} height={Y(0) - Y(50)} fill="rgba(233,162,59,.07)" />
          <rect x={X(0)} y={Y(50)} width={X(50) - X(0)} height={Y(0) - Y(50)} fill="rgba(230,103,103,.07)" />
          <line x1={X(50)} x2={X(50)} y1={Y(100)} y2={Y(0)} stroke="rgba(221,240,247,.2)" strokeDasharray="4 4" />
          <line x1={X(0)} x2={X(100)} y1={Y(50)} y2={Y(50)} stroke="rgba(221,240,247,.2)" strokeDasharray="4 4" />
          <text x="225" y="292" textAnchor="middle" fontSize="10" fill="#86A6B6">Cómo sabe: dominio de los temas →</text>
          <text x="10" y="145" textAnchor="middle" fontSize="10" fill="#86A6B6" transform="rotate(-90 10 145)">Cómo se siente →</text>
          {all.slice(0, -1).map((s, i) => (
            <circle key={i} className="move" r="5" fill="rgba(221,240,247,.55)" style={{ transform: `translate(${X(s.x)}px, ${Y(s.y)}px)` }} />
          ))}
          <g className="move" style={{ transform: `translate(${X(sofia.x)}px, ${Y(sofia.y)}px)` }}>
            <circle r="8" fill="#57D3FF" stroke="#071925" strokeWidth="2" /><circle r="8" fill="none" stroke="#57D3FF" className="ring" />
            <text x="12" y="4" fontSize="11" fontWeight="800" fill="#DDF0F7">Sofía R.</text>
          </g>
        </svg>
      </div>
      <div className="dash-panel flex min-h-0 flex-col gap-2 p-3">
        <Head title="Grupo 5.º A, franquicia CDMX" sub="23 estudiantes" right={<Live />} />
        {Q.map(([l, tone], i) => (
          <div key={l} className="flex items-center justify-between gap-2 text-[12px]"><Status tone={tone}>{l}</Status><span className="num font-bold">{counts[i]}</span></div>
        ))}
        <p className="mt-auto rounded-md bg-[rgba(87,211,255,.1)] p-2 text-[11.5px] leading-snug">Sofía domina los temas pero llegaba con ansiedad. Con acompañamiento, su bienestar sube semana a semana.</p>
      </div>
    </div>
  );
}

/* 2 · Perfil del estudiante (radar: saber vs sentir por tema) */
const TEMAS = ['Fracciones', 'Geometría', 'Cálculo mental', 'Problemas', 'Álgebra', 'Estadística'];
export function NeuroPerfil() {
  const t = useTick(2400);
  const saber = [82, 64, 90, 58, 71, 77].map((v, i) => v + wave(t, i + 1, 3));
  const sentir = [55, 70, 62, 40, 48, 74].map((v, i) => v + wave(t, i + 3, 4) + Math.min(8, (t % 12)));
  const P = (vals) => vals.map((v, i) => { const a = (Math.PI * 2 * i) / 6 - Math.PI / 2; const r = (v / 100) * 105; return `${(140 + Math.cos(a) * r).toFixed(1)},${(130 + Math.sin(a) * r).toFixed(1)}`; }).join(' ');
  const gaps = TEMAS.map((n, i) => ({ n, g: saber[i] - sentir[i] })).sort((a, b) => b.g - a.g);
  return (
    <div className="grid h-full grid-cols-1 gap-3 p-3 sm:grid-cols-[1.2fr_1fr]">
      <div className="dash-panel flex min-h-0 flex-col p-3">
        <Head title="Sofía R., 5.º grado" sub="Perfil personalizado" right={<Legend />} />
        <svg viewBox="0 0 280 260" className="mx-auto h-full w-full max-w-[320px]" role="img" aria-label="Radar por tema: dominio frente a confianza">
          {[0.25, 0.5, 0.75, 1].map((f) => <polygon key={f} points={P(Array(6).fill(f * 100))} fill="none" stroke="rgba(221,240,247,.1)" />)}
          {TEMAS.map((n, i) => { const a = (Math.PI * 2 * i) / 6 - Math.PI / 2; return <text key={n} x={140 + Math.cos(a) * 124} y={133 + Math.sin(a) * 118} textAnchor="middle" fontSize="10" fill="#86A6B6">{n}</text>; })}
          <polygon points={P(saber)} fill="rgba(46,155,214,.22)" stroke={SABER} strokeWidth="2" className="grow" style={{ transformOrigin: '140px 130px', transformBox: 'view-box', transition: 'all 900ms' }} />
          <polygon points={P(sentir)} fill="rgba(199,129,31,.2)" stroke={SENTIR} strokeWidth="2" className="grow" style={{ '--d': '250ms', transformOrigin: '140px 130px', transformBox: 'view-box' }} />
        </svg>
      </div>
      <div className="dash-panel flex min-h-0 flex-col gap-2 p-3">
        <div className="dim text-[11px] font-semibold">Donde sabe más de lo que cree</div>
        {gaps.slice(0, 3).map((g) => (
          <div key={g.n} className="text-[12px]">
            <div className="flex justify-between"><span className="font-semibold">{g.n}</span><span className="num dim">brecha {Math.round(g.g)}</span></div>
            <div className="mt-1 h-1.5 rounded bg-white/10"><div className="bar-live-x h-full rounded" style={{ background: SENTIR, transform: `scaleX(${Math.max(0, g.g) / 40})` }} /></div>
          </div>
        ))}
        <div className="mt-auto rounded-md bg-[rgba(199,129,31,.12)] p-2 text-[11.5px] leading-snug">Recomendación: reforzar la confianza en problemas con ejercicios cortos y logros visibles, no con más teoría.</div>
      </div>
    </div>
  );
}

/* 3 · Clase en vivo con pizarra y evaluación instantánea */
export function NeuroClase() {
  const t = useTick(900);
  const step = t % 14;
  const answers = [3, 15, 2, 1].map((v, i) => Math.min(v, Math.floor((step / 10) * v + (i === 1 ? 1 : 0))));
  const total = answers.reduce((a, b) => a + b, 0);
  const lines = [['x² − 5x + 6 = 0', '#DDF0F7'], ['(x − 2)(x − 3) = 0', '#8CCBEA'], ['x = 2,  x = 3', '#8BE07F']];
  return (
    <div className="grid h-full grid-cols-1 gap-3 p-3 sm:grid-cols-[1.5fr_1fr]">
      <div className="dash-panel relative min-h-0 overflow-hidden p-4">
        <Head title="Clase en vivo, álgebra inicial" sub="Pizarra de la profesora" right={<Live label="12 conectados" />} />
        <div className="mt-4 space-y-3">
          {lines.map(([s, c], i) => (
            <div key={s} className="overflow-hidden">
              <div className="whitespace-nowrap text-[clamp(20px,3.4vw,30px)] italic" style={{ fontFamily: 'Georgia, serif', color: c,
                clipPath: `inset(0 ${step > i * 3 ? 0 : 100}% 0 0)`, transition: 'clip-path 1400ms cubic-bezier(.23,1,.32,1)' }}>{s}</div>
            </div>
          ))}
        </div>
        <svg viewBox="0 0 200 20" className="mt-1 h-4 w-[60%]"><path d="M2 12 C 50 18, 120 6, 196 12" fill="none" stroke="#4FBF6A" strokeWidth="3" strokeLinecap="round" pathLength="1"
              strokeDasharray="1" strokeDashoffset={step > 8 ? 0 : 1} style={{ transition: 'stroke-dashoffset 1s' }} /></svg>
      </div>
      <div className="dash-panel flex min-h-0 flex-col gap-2 p-3">
        <Head title="Pregunta rápida" sub="¿Cuáles son las raíces?" />
        {[['A', 'x = 1, x = 6'], ['B', 'x = 2, x = 3'], ['C', 'x = −2, x = −3'], ['D', 'x = 5, x = 6']].map(([k, s], i) => (
          <div key={k} className="text-[12px]">
            <div className="flex justify-between"><span><b>{k}</b> {s}</span><span className="num">{answers[i]}</span></div>
            <div className="mt-1 h-2 rounded bg-white/10"><div className="bar-live-x h-full rounded" style={{ background: i === 1 ? '#4FBF6A' : '#5E7E90', transform: `scaleX(${answers[i] / 15})` }} /></div>
          </div>
        ))}
        <div className="mt-auto"><Kpi label={`respondieron ${total} de 21`} tone="ok"><Count value={total ? (answers[1] / total) * 100 : 0} suffix=" % acierto" dur={500} /></Kpi></div>
      </div>
    </div>
  );
}

/* 4 · Bitácora emocional frente a resultados */
const NOTA = [62, 64, 66, 61, 70, 74, 72, 78, 81, 84];
const CONF = [48, 52, 50, 34, 47, 58, 63, 67, 72, 78];
const MOODS = ['Muy nerviosa', 'Nerviosa', 'Tranquila', 'Contenta', 'Muy segura'];
export function NeuroBitacora() {
  const t = useTick(1600);
  const X = (i) => 30 + i * (380 / 9); const Y = (v) => 210 - ((v - 25) / 65) * 190;
  const path = (a) => a.map((v, i) => `${i ? 'L' : 'M'}${X(i).toFixed(1)} ${Y(v).toFixed(1)}`).join(' ');
  return (
    <div className="flex h-full flex-col gap-3 p-3">
      <Head title="Bitácora emocional de Sofía" sub="Registro antes de cada clase y nota de la sesión" right={<Legend />} />
      <div className="dash-panel min-h-0 flex-1 p-2">
        <svg viewBox="0 0 430 225" className="h-full w-full" preserveAspectRatio="none" role="img" aria-label="La confianza cayó antes de la evaluación y luego subió junto con la nota">
          {[40, 60, 80].map((v) => <g key={v}><line x1="30" x2="410" y1={Y(v)} y2={Y(v)} stroke="rgba(221,240,247,.08)" /><text x="24" y={Y(v) + 3} textAnchor="end" fontSize="9" fill="#86A6B6">{v}</text></g>)}
          <rect x={X(3) - 12} y="10" width="24" height="200" fill="rgba(230,103,103,.1)" />
          <text x={X(3)} y="22" textAnchor="middle" fontSize="9.5" fill="#FF8F82">Evaluación</text>
          <path d={path(NOTA)} fill="none" stroke={SABER} strokeWidth="2.5" pathLength="1" className="draw" />
          <path d={path(CONF)} fill="none" stroke={SENTIR} strokeWidth="2.5" pathLength="1" className="draw" style={{ '--d': '300ms' }} />
          <circle cx={X(9)} cy={Y(84)} r="4" fill={SABER} className="fade-in" style={{ '--d': '1300ms' }} />
          <circle cx={X(9)} cy={Y(78)} r="4" fill={SENTIR} className="fade-in" style={{ '--d': '1500ms' }} />
        </svg>
      </div>
      <div className="flex flex-wrap items-center gap-1.5 text-[11.5px]">
        <span className="dim mr-1">Hoy, antes de clase:</span>
        {MOODS.map((m, i) => (
          <span key={m} className={`rounded-full border px-2.5 py-1 transition-colors duration-300 ${i === 2 + (t % 3) ? 'border-[#C7811F] bg-[rgba(199,129,31,.2)] text-white' : 'border-white/10 dim'}`}>{m}</span>
        ))}
      </div>
    </div>
  );
}

/* 5 · Ruta de aprendizaje personalizada */
const NODES = [['Fracciones equivalentes', 70, 200], ['Suma de fracciones', 120, 140], ['Problemas con fracciones', 210, 180],
  ['Decimales', 290, 110], ['Porcentajes', 370, 150], ['Proporciones', 430, 70]];
export function NeuroRuta() {
  const t = useTick(1500);
  const cur = 2 + (t % 4);
  const d = NODES.map(([, x, y], i) => `${i ? 'L' : 'M'}${x} ${y}`).join(' ');
  return (
    <div className="grid h-full grid-cols-1 gap-3 p-3 sm:grid-cols-[1.6fr_1fr]">
      <div className="dash-panel relative min-h-0 overflow-hidden p-2">
        <svg viewBox="0 0 470 250" className="h-full w-full" role="img" aria-label="Ruta de aprendizaje con temas completados y el siguiente recomendado">
          <path d={d} fill="none" stroke="rgba(221,240,247,.15)" strokeWidth="3" strokeDasharray="5 6" />
          <path d={NODES.slice(0, cur + 1).map(([, x, y], i) => `${i ? 'L' : 'M'}${x} ${y}`).join(' ')} fill="none" stroke="#57D3FF" strokeWidth="3" />
          {NODES.map(([n, x, y], i) => {
            const done = i < cur; const now = i === cur;
            return (
              <g key={n} transform={`translate(${x} ${y})`}>
                {now && <circle r="14" fill="none" stroke="#57D3FF" className="ring" />}
                <circle r={now ? 13 : 10} fill={done ? '#4FBF6A' : now ? '#57D3FF' : '#123247'} stroke="#071925" strokeWidth="3" style={{ transition: 'r 300ms, fill 300ms' }} />
                <text y={i % 2 ? -20 : 28} textAnchor="middle" fontSize="10.5" fontWeight={now ? 800 : 500} fill={now ? '#DDF0F7' : '#86A6B6'}>{n}</text>
              </g>
            );
          })}
        </svg>
      </div>
      <div className="flex min-h-0 flex-col gap-2">
        <Kpi label="Temas dominados" tone="ok"><Count value={cur} suffix=" de 6" dur={500} /></Kpi>
        <div className="dash-panel p-3 text-[12px]">
          <div className="dim text-[11px] font-semibold">Siguiente actividad recomendada</div>
          <div key={cur} className="rise mt-1 font-semibold">{NODES[cur][0]}: reto de 10 minutos con pistas</div>
        </div>
        <div className="dash-panel hidden p-3 text-[11.5px] leading-snug sm:block">
          <Status tone="info">Aviso a la familia</Status>
          <p className="dim mt-1.5">Resumen semanal para padres: avance académico y cómo se sintió Sofía en clase.</p>
        </div>
      </div>
    </div>
  );
}

export const neuroScreens = [
  { t: 'Sentir vs saber', C: NeuroMatriz }, { t: 'Perfil', C: NeuroPerfil }, { t: 'Clase en vivo', C: NeuroClase },
  { t: 'Bitácora', C: NeuroBitacora }, { t: 'Ruta', C: NeuroRuta },
];
